/**
 * Chart, Table and Number widgets (screenshots/67, 69, 70; sizes 71): a dataset query (`/api/datasets/:id`, §2.11 Query)
 * rendered by ChartWidget (Apache ECharts), or as an HTML table whose columns drop right-to-left when the widget gets
 * smaller (the first column always stays). Money datasets always show their caveats ("Money near a vote…" + the
 * lobbyist-overlap note).
 *
 * Chart behaviors (§2.14, screenshots/88–89), per the widget's Interact settings:
 * - Click → Filter the overview: sets the page cross-filter; every other widget with that field re-queries
 *   (withCrossFilter) and this chart only highlights. Click the same mark again to clear.
 * - Click → Drill down: the next grouping opens in this widget (interactions.ts DRILL_PATHS) with breadcrumbs; Back or
 *   Esc goes up a level.
 * - Zoom & brush on Line / Area / Column over time: dataZoom inside + a slider at L; the range is saved to the widget
 *   (config.zoom) and comes back from shared links (`?z=w9:20-60`). "Reset zoom" restores it.
 * - Data states: empty, couldn't load, slow, too many, live connection lost, not for this viewer (DataStates.tsx).
 */
import { useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { createPortal } from "react-dom";
import type * as echarts from "echarts/core";
import { navigate } from "../../app/router";
import { useConnection } from "../../stores/connectionStore";
import { ChartWidget } from "../charts/ChartWidget";
import { fits, keepOrSuggest, type ChartType } from "../charts/chartTypes";
import { DATASETS, bindingToQuery, type ChartSettings, type DataBinding } from "../charts/chartSettings";
import { drillBack, drillInto, nextGrouping, parseZoom, toggleCrossFilter, tooBig, withCrossFilter, MAX_GROUPS, type DrillStep } from "../charts/interactions";
import { entryOf } from "../catalog";
import { chartEditorStore, overviewCrossFilter, setOverviewFilter, themeById, themeStore, useOverviewFilter } from "../chartEditorStore";
import { useThemes } from "../charts/themeStore";
import { overviewStore, useOverview } from "../overviewStore";
import type { DatasetColumn, DatasetResult } from "../types";
import { chartInstances, filtersText, widgetViews } from "../widgetViews";
import { DrillCrumbs, ErrorBanner, LoadingChip, OfflineChip, ResetZoom, StateView, TooBigBanner } from "./DataStates";
import type { WidgetProps } from "./props";
import { withPageFilters } from "../../analytics/pageFilters";
import { SLOW_LINE_MS, SLOW_TEXT_MS, useDatasetQuery } from "./useDatasetQuery";

const usd = (v: number) => `$${Math.round(v).toLocaleString("en-US")}`;
const cell = (v: unknown, c: DatasetColumn) => (typeof v === "number" ? (c.format === "usd" ? usd(v) : v.toLocaleString("en-US")) : String(v ?? "—"));
/** Plural nouns for a grouping field ("All alderpersons", "3,412 donors"). */
const NOUN: Record<string, string> = { recipient: "alderpersons", member: "members", client: "clients", lobbyist: "lobbyists", matter: "items", category: "categories",
  name: "names", topic: "topics", sponsor: "sponsors", ward: "wards", body: "bodies", item: "items", record: "items", contributorType: "contributor types" };
const LEVEL: Record<string, string> = { date: "by year", quarter: "by quarter", contributorType: "by contributor type", topic: "by topic", member: "by member", stage: "by stage", sponsor: "by sponsor" };
const ZOOMABLE = new Set(["line", "area", "column"]);
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const tLabel = (t: string) => { const m = /^\d{4}-(\d{2})/.exec(t); return m ? MONTH[Number(m[1]) - 1] : t; };
const withSources = (b: DataBinding, sources?: "all" | "company") =>
  sources === "company" ? { ...b, filters: [...b.filters, { field: "contributorType", op: "is" as const, value: "company" }] } : b;
/** Rows the chart would draw (the shape decides: a split/ward/… shape can carry its rows without table rows). */
const rowCount = (ds: DatasetResult) => {
  const sh = ds.shape as unknown as Record<string, unknown>;
  for (const k of ["points", "rows", "parts", "members", "nodes", "days", "events", "series", "roots"]) if (Array.isArray(sh[k])) return (sh[k] as unknown[]).length;
  if (sh.values && typeof sh.values === "object") return Object.keys(sh.values as object).length;
  return ds.rows.length || 1;
};
/** Shared links: `?z=w9:20-60` opens the widget zoomed to that range. */
const linkZoom = typeof location === "undefined" ? {} : parseZoom(new URLSearchParams(location.search).get("z"));

export function DataWidget({ item, w, h, group, snapshot, slot }: WidgetProps) {
  const base = useOverview((s) => s.datasets[item.id]);
  const justAdded = useOverview((s) => s.justAdded.includes(item.id));
  const themes = useThemes(themeStore, (s) => s.themes);
  const filter = useOverviewFilter();
  const conn = useConnection((s) => s.status);
  const snapAt = useConnection((s) => s.snapshotAt);
  const settings = item.config.settings;
  const [drill, setDrill] = useState<DrillStep[]>([]);
  // Analytics dashboard filters (84) come first, like the Worker's first-paint queries (analytics/pageFilters.ts).
  const pageFilters = useOverview((s) => s.pageFilters);
  const root = useMemo(() => (settings ? withSources(withPageFilters(settings.data, pageFilters), item.config.sources) : null), [settings, item.config.sources, pageFilters]);
  const binding = useMemo(() => {
    if (!root) return null;
    let b = withCrossFilter(root, filter, item.id).binding;
    for (const st of drill) b = drillInto(b, st) ?? b;
    return b;
  }, [root, filter, drill, item.id]);
  const url = binding ? bindingToQuery(binding) : null;
  const q = useDatasetQuery(url, base);
  const ds = q.ds;
  const title = item.config.title ?? settings?.labels.title ?? entryOf(item.type).title;
  // Row extras (ward, company, lobbyists…) for label tokens and Table columns (87).
  const vars = useMemo(() => {
    const out: Record<string, Record<string, string | number>> = {};
    for (const r of ds?.rows ?? []) out[String(r.key)] = Object.fromEntries(Object.entries(r).filter(([k, v]) => k !== "key" && v != null)) as Record<string, string | number>;
    return out;
  }, [ds]);

  // The ⋯ menu exports what's on screen (widgetViews) from the live chart (chartInstances).
  const chartRef = useMemo(() => ({
    get current() { return chartInstances.get(item.id) ?? null; },
    set current(c: echarts.ECharts | null) { if (c) chartInstances.set(item.id, c); else chartInstances.delete(item.id); },
  }) as MutableRefObject<echarts.ECharts | null>, [item.id]);
  useEffect(() => {
    if (ds && binding) widgetViews.set(item.id, { ds, binding, title, filters: filtersText(binding) });
    return () => { widgetViews.delete(item.id); };
  }, [ds, binding, title, item.id]);

  // Zoom: saved range (debounced while dragging the slider) or the shared link's.
  const zoomTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const zoomable = !!settings?.interact.zoom && ZOOMABLE.has(settings.type) && ds?.shape.kind === "time";
  const saved = linkZoom[item.id] ?? item.config.zoom;
  const onZoom = (r: [number, number]) => {
    clearTimeout(zoomTimer.current);
    const next: [number, number] = [Math.round(r[0]), Math.round(r[1])];
    if (saved && saved[0] === next[0] && saved[1] === next[1]) return;
    zoomTimer.current = setTimeout(() => overviewStore.getState().setConfig(item.id, { zoom: next[0] <= 0 && next[1] >= 100 ? undefined : next }), 400);
  };
  useEffect(() => () => clearTimeout(zoomTimer.current), []);

  // Esc goes up one drill level (before the page's Esc clears the cross-filter).
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!drill.length || !settings) return;
    const k = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || chartEditorStore.getState().open) return;
      if (!host.current?.closest(".ov-widget")?.matches(":hover, :focus-within")) return;
      e.preventDefault();
      setDrill((d) => drillBack(settings.data, d).path);
    };
    addEventListener("keydown", k, true);
    return () => removeEventListener("keydown", k, true);
  }, [drill.length, settings]);

  if (!settings || !binding) return <p className="ov-empty-data">{entryOf(item.type).tile}</p>;
  const def = DATASETS[settings.data.datasetId];
  if (q.forbidden || (def.groups && !def.groups.includes(group))) return <StateView state={{ kind: "forbidden", reason: "group" }} />;
  if (!ds) return null;

  const field = binding.mapping.category ?? "label";
  const labelOf = (key: string) => String(ds.rows.find((r) => String(r.key) === key)?.label ?? key);
  const onSelect = (key: string) => {
    const it = settings.interact;
    if (it.click === "none") return;
    if (it.click === "filter" && binding.mapping.category) {
      setOverviewFilter(toggleCrossFilter(overviewCrossFilter.getState().filter, { field, value: key, label: labelOf(key), sourceWidgetId: item.id }));
      return;
    }
    if (it.click === "drill" && drillInto(binding, { field, value: key, label: labelOf(key) })) { setDrill([...drill, { field, value: key, label: labelOf(key) }]); return; }
    const href = ds.links?.[key]; if (href) navigate(href);
  };

  // States (88)
  const userFilters = binding.filters.filter((f) => f.field !== "contributorType" || item.config.sources !== "company");
  const last = userFilters.at(-1);
  const crossApplied = !!filter && withCrossFilter(root!, filter, item.id).applied;
  const empty = rowCount(ds) === 0 && !q.loadingMs;
  const step = drill.find((d) => d.field === last?.field && String(d.value) === String(last?.value));
  const removable = empty && last ? { filter: step ? step.label : crossApplied && last.field === filter!.field ? filter!.label : last.field === "ward" ? `Ward ${last.value}` : String(last.value),
    wouldReturn: base?.total ?? 0 } : undefined;
  const big = !drill.length && ds.total > MAX_GROUPS && ds.shape.kind === "category" ? tooBig(ds.total, binding, { ward: 50, contributorType: 3 }) : null;
  const level = drill.length ? (nextGrouping({ ...binding, mapping: { ...binding.mapping, category: drill.at(-1)!.field } }) ?? field) : field;
  const zoomRange = Array.isArray(saved) && ds.shape.kind === "time" && ds.shape.points.length
    ? `${tLabel(ds.shape.points[Math.floor((saved[0] / 100) * (ds.shape.points.length - 1))].t)}–${tLabel(ds.shape.points[Math.ceil((saved[1] / 100) * (ds.shape.points.length - 1))].t)}` : null;
  const offline = conn === "reconnecting" && settings.data.refresh === "live";
  const chips = (
    <>
      {q.loadingMs > SLOW_TEXT_MS && <LoadingChip ms={q.loadingMs} onCancel={q.cancel} />}
      {offline && snapAt && <OfflineChip at={new Date(snapAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} />}
      {zoomable && zoomRange && <ResetZoom range={zoomRange} onReset={() => overviewStore.getState().setConfig(item.id, { zoom: undefined })} />}
    </>
  );
  const card = slot?.closest(".ov-widget");
  const edit = () => chartEditorStore.getState().edit(item.id);
  if (empty && !q.error) return <>{slot && createPortal(chips, slot)}<StateView state={{ kind: "empty", removable }} onEdit={edit}
    onRemove={() => { if (crossApplied) setOverviewFilter(null); else if (drill.length) setDrill(drill.slice(0, -1)); else edit(); }} /></>;

  const isTable = item.type === "table" || settings.type === "table";
  const money = ds.datasetId === "contributions" && !!ds.totals;
  // A drilled level keeps the type if it still fits its rows; years draw as columns (89).
  const drawType: ChartType = item.type === "number" ? "number" : fits(settings.type, ds.shape) ? settings.type
    : drill.length && fits("column", ds.shape) ? "column" : keepOrSuggest(settings.type, ds.shape);
  // The source of the page filter only highlights: the picked mark keeps its color, the others go muted (89).
  const chartSettings: ChartSettings = filter?.sourceWidgetId === item.id && settings.colors.mode !== "category"
    ? { ...settings, colors: { ...settings.colors, mode: "highlight", highlight: { key: filter.value, color: settings.colors.mode === "single" ? settings.colors.single : "chart-1", others: "muted", label: true } } }
    : settings;
  return (
    <div ref={host} className={`ov-data${q.error ? " is-stale" : ""}`}>
      {slot && createPortal(chips, slot)}
      {card && q.loadingMs > SLOW_LINE_MS && createPortal(<span className="ov-data__progress" aria-hidden />, card)}
      {drill.length > 0 && <DrillCrumbs root={NOUN[root!.mapping.category ?? ""] ?? "rows"} steps={drill.map((d) => d.label)} level={LEVEL[level] ?? `by ${level}`}
        sample={!!ds.sample} onTo={(depth) => setDrill(drill.slice(0, Math.max(0, depth)))} />}
      {!drill.length && (item.config.note || money) && (
        <div className="ov-data__controls">
          <span className="ov-data__note">{item.config.note ?? `${ds.totals?.name ?? ""} · ${item.config.sources === "company" ? "company only" : "all sources"}`}</span>
          {money && item.config.sources && w >= 6 && (
            <span className="ov-switch" role="radiogroup" aria-label="Sources">
              {(["all", "company"] as const).map((v) => (
                <button key={v} role="radio" aria-checked={(item.config.sources ?? "all") === v}
                  onClick={() => overviewStore.getState().setConfig(item.id, { sources: v })}>{v === "all" ? "All sources" : "Company only"}</button>
              ))}
            </span>
          )}
        </div>
      )}
      {big?.kind === "tooBig" && <TooBigBanner groups={big.groups} shown={big.shown} noun={NOUN[field] ?? "groups"} suggestions={big.suggestions}
        onPick={(sg) => overviewStore.getState().setConfig(item.id, { settings: sg.type === "table" ? { ...settings, type: "table" }
          : { ...settings, data: { ...settings.data, mapping: { ...settings.data.mapping, category: sg.groupBy } } } })} />}
      {q.error && <ErrorBanner what={DATASETS[settings.data.datasetId].label} at={q.error.at} id={q.error.id} onRetry={q.retry} />}
      {isTable && !settings.table ? <DataTable ds={ds} w={w} h={h} onSelect={onSelect} /> : (
        <div className="ov-data__chart">
          <ChartWidget type={drawType} shape={ds.shape} w={w} h={h} title={title}
            source={ds.sample ? { kind: "sample" } : { kind: "real", text: ds.caveats.join(" ") }} settings={chartSettings}
            config={zoomable ? { ...chartSettings.config, zoom: saved ?? true } : chartSettings.config} onZoom={zoomable ? onZoom : undefined}
            chartRef={chartRef} viewer={snapshot?.viewer.ward ? { ward: snapshot.viewer.ward } : undefined}
            allRowsTotal={ds.totals ? ds.totals.company + ds.totals.lobbyists : undefined} vars={vars}
            justAdded={justAdded} onSelect={onSelect} theme={themeById(themes, settings.themeId)} />
        </div>
      )}
    </div>
  );
}

/** Table (67 "Top lobbying clients on housing"): header row, N rows that fit, columns dropped right-to-left by width. */
function DataTable({ ds, w, h, onSelect }: { ds: DatasetResult; w: number; h: number; onSelect: (key: string) => void }) {
  const keep = w >= 6 ? ds.columns.length : w >= 4 ? Math.min(3, ds.columns.length) : Math.min(2, ds.columns.length);
  const cols = ds.columns.slice(0, keep);
  const rows = ds.rows.slice(0, Math.max(1, Math.floor((h * 88 - 16 - 54 - 26) / 40)));
  // A dataset with no producer yet is sample data: say so, as the chart footer does (ChartWidget "Sample data").
  return (<>
    <table className="ov-table ov-table--data">
      <thead><tr>{cols.map((c) => <th key={c.id} className={c.align === "right" ? "is-num" : undefined}>{c.label}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={String(r.key)} onClick={() => onSelect(String(r.key))} className={ds.links?.[String(r.key)] ? "is-link" : undefined}>
            {cols.map((c, n) => (
              <td key={c.id} className={[c.align === "right" ? "is-num" : "", n === 0 ? "ov-table__strong" : "", c.format === "usd" ? "ov-table__money" : c.align === "right" && n === 2 ? "ov-table__muted" : ""].join(" ").trim() || undefined}>
                {n === 0 && ds.links?.[String(r.key)]
                  ? <a href={ds.links[String(r.key)]} onClick={(e) => { e.preventDefault(); onSelect(String(r.key)); }}>{cell(r[c.id], c)}</a>
                  : cell(r[c.id], c)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </>);
}
